/**
 * DemoTotpHint.jsx — Tester-friendly TOTP code display for the 2FA screen.
 *
 * Only renders when:
 *   - `REACT_APP_SHOW_DEMO_CREDENTIALS=true` on the frontend, AND
 *   - The backend env flag `K12ERP_DEMO_TOTP_HINT=true` is on (the
 *     `/api/auth/2fa/demo-hint/` endpoint returns 200 instead of 404).
 *
 * Polls every second to keep the countdown live and refreshes the code
 * when the 30-second window rolls over. One-click Copy button.
 *
 * If both the backend and frontend flags are off, the component renders
 * absolutely nothing — production deployments are unaffected.
 */
import React, { useEffect, useState } from 'react';
import { Copy, Check, Clock } from 'lucide-react';
import axios from 'axios';

const apiBase = process.env.REACT_APP_BACKEND_URL;
const SHOW = process.env.REACT_APP_SHOW_DEMO_CREDENTIALS === 'true';

export default function DemoTotpHint() {
  const [hint, setHint] = useState(null);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const fetchHint = async () => {
    try {
      const res = await axios.get(`${apiBase}/api/auth/2fa/demo-hint/`);
      if (res?.data?.enabled) {
        setHint(res.data);
        setCountdown(res.data.expires_in);
      } else {
        setHint(null);
      }
    } catch {
      setHint(null);
    }
  };

  useEffect(() => {
    if (!SHOW) return;
    fetchHint();
  }, []);

  // 1 Hz tick: when countdown drops to 0, re-fetch the next code.
  useEffect(() => {
    if (!SHOW || !hint) return;
    const t = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchHint();
          return hint.period || 30;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [hint]);

  const copy = () => {
    if (!hint?.code) return;
    const code = hint.code;
    const showCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    // Primary UX: auto-fill the visible verification input AND auto-submit
    // (much friendlier than the copy-paste-click-verify ritual that
    // expires in 30s). Falls back to clipboard if the input cannot be
    // located.
    const filled = autofillVerificationInput(code);
    if (filled) {
      showCopied();
      // Trigger an auto-submit after a tiny pause so the user can see
      // the code appear in the input first.
      setTimeout(() => triggerVerifySubmit(), 150);
      return;
    }

    // Modern Async Clipboard API (HTTPS + permission required).
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(showCopied).catch(() => {
        legacyCopy(code);
        showCopied();
      });
      return;
    }
    legacyCopy(code);
    showCopied();
  };

  const autofillVerificationInput = (code) => {
    // Try the well-known testids first, then fall back to any 6-digit
    // numeric input. Returns true if we filled an input.
    const candidates = [
      document.querySelector('[data-testid="tfa-code-input"]'),
      document.querySelector('[data-testid="tfa-verify-code-input"]'),
      document.querySelector('[data-testid="login-2fa-code-input"]'),
      document.querySelector('input[autocomplete="one-time-code"]'),
      document.querySelector('input[name="code"]'),
      document.querySelector('input[maxlength="6"]'),
    ].filter(Boolean);
    if (!candidates.length) return false;
    const input = candidates[0];
    // Use the native setter so React notices the change (controlled inputs
    // require dispatching the synthetic event manually).
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value',
    ).set;
    setter.call(input, code);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
    return true;
  };

  const triggerVerifySubmit = () => {
    // Prefer form.requestSubmit() — it fires the React onSubmit handler
    // without requiring the submit button to be enabled.  This is safe
    // because handle2FASubmit reads the code from the DOM as a fallback
    // (so even if React state hasn't flushed yet, the correct code is sent).
    const inp =
      document.querySelector('[data-testid="tfa-code-input"]') ||
      document.querySelector('[data-testid="tfa-verify-code-input"]') ||
      document.querySelector('[data-testid="login-2fa-code-input"]') ||
      document.querySelector('input[autocomplete="one-time-code"]') ||
      document.querySelector('input[name="code"]') ||
      document.querySelector('input[maxlength="6"]');
    const form = inp?.closest('form');
    if (form?.requestSubmit) {
      form.requestSubmit();
      return;
    }
    // Fallback for browsers without requestSubmit support
    const button =
      document.querySelector('[data-testid="tfa-verify-btn"]') ||
      document.querySelector('[data-testid="tfa-verify-submit"]') ||
      document.querySelector('[data-testid="login-2fa-submit"]') ||
      form?.querySelector('button[type="submit"]');
    if (button) button.click();
  };

  const legacyCopy = (text) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      if (typeof document.execCommand === 'function') {
        document.execCommand('copy');
      }
      document.body.removeChild(ta);
    } catch {
      // Best-effort. The visual "Copied" feedback still fires.
    }
  };

  if (!SHOW || !hint?.code) return null;

  return (
    <div
      className="rounded-md border border-blue-200 bg-blue-50 p-3 mb-4"
      data-testid="demo-totp-hint"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">
            One-click code
          </div>
          <button
            type="button"
            onClick={copy}
            className="mt-1 flex items-center gap-2 text-2xl font-mono tracking-widest text-slate-900 hover:text-blue-700 transition-colors"
            data-testid="demo-totp-code-btn"
            title="Click to auto-fill and sign in"
          >
            <span>{hint.code}</span>
            {copied ? (
              <Check size={18} className="text-emerald-600" />
            ) : (
              <Copy size={18} className="text-blue-600" />
            )}
          </button>
        </div>
        <div className="flex items-center gap-1 text-xs text-blue-700 whitespace-nowrap">
          <Clock size={12} />
          <span data-testid="demo-totp-countdown">{countdown}s</span>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-slate-600 leading-snug">
        Click the code to auto-fill the verification box and sign in.
        {' '}Works for {hint.accounts?.slice(0, 2).join(', ')}
        {hint.accounts && hint.accounts.length > 2
          ? ` (+${hint.accounts.length - 2} more)`
          : ''}.
      </div>
    </div>
  );
}
